// MsspContactStep.js — MSSP contact person details + CAPTCHA.
// Field layout:
//   First Name | Last Name  (2-col grid)
//   Organization Domain
//   Work Email
//   Phone Number
//   CAPTCHA image challenge

import React from 'react';
import Input from '../../Input';

// Six dark-navy hues — must stay in sync with MsspSignup.js CAPTCHA_CHAR_COLORS.
const CHAR_COLORS = [
  '#1a3a5c', '#243068', '#1e3c62', '#2a3870', '#163254', '#1c2e58',
];

// ── Apple-style distorted-character CAPTCHA image rendered as inline SVG ────
// Props come from formData so the image is stable across React re-renders
// (randomisation is done once in generateCaptcha in MsspSignup.js, not each render).
const CaptchaImage = ({ chars, charData, noiseLines }) => {
  const W = 240, H = 72;
  const PAD_X = 20;
  const charStep = (W - PAD_X * 2) / chars.length;

  return (
    <svg
      width="100%"
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      {/* Background */}
      <rect width={W} height={H} fill="#e8eef7" />

      {/* Subtle noise lines */}
      {(noiseLines || []).map((l, i) => (
        <line
          key={i}
          x1={l.x1} y1={l.y1}
          x2={l.x2} y2={l.y2}
          stroke="#b8cde4"
          strokeWidth={0.85}
          opacity={0.6}
        />
      ))}

      {/* Characters */}
      {chars.split('').map((ch, i) => {
        const d = (charData || [])[i] || {};
        const cx = PAD_X + i * charStep + charStep / 2;
        const cy = H / 2 + (d.yOffset || 0);
        return (
          <text
            key={i}
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={d.fontSize || 24}
            fontWeight="700"
            fill={CHAR_COLORS[d.colorIdx ?? (i % CHAR_COLORS.length)]}
            transform={`rotate(${d.rotation || 0}, ${cx}, ${cy})`}
            fontFamily="Georgia, 'Times New Roman', serif"
            style={{ userSelect: 'none' }}
          >
            {ch}
          </text>
        );
      })}
    </svg>
  );
};

// ── Main step component ───────────────────────────────────────────────────────
const MsspContactStep = ({ formData, onChange, onEmailBlur, onRefreshCaptcha, errors }) => {

  // Speak characters via Web Speech API (accessibility)
  const handleAudio = () => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const text = (formData.captchaQuestion || '').split('').join(' … ');
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate  = 0.6;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  };

  return (
    <div className="space-y-4">

      {/* First Name + Last Name */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="First Name"
          name="userFirstName"
          value={formData.userFirstName}
          onChange={onChange}
          required
          error={errors.userFirstName}
          placeholder="First name"
        />
        <Input
          label="Last Name"
          name="userLastName"
          value={formData.userLastName}
          onChange={onChange}
          required
          error={errors.userLastName}
          placeholder="Last name"
        />
      </div>

      <Input
        label="Organization Domain"
        name="userDomain"
        value={formData.userDomain}
        onChange={onChange}
        required
        error={errors.userDomain}
        placeholder="yourcompany.com"
      />

      <Input
        label="Work Email"
        name="userEmail"
        type="email"
        value={formData.userEmail}
        onChange={onChange}
        onBlur={onEmailBlur}
        required
        error={errors.userEmail}
        placeholder="you@yourcompany.com"
      />

      <Input
        label="Phone Number"
        name="userPhoneNumber"
        type="tel"
        value={formData.userPhoneNumber}
        onChange={onChange}
        required
        error={errors.userPhoneNumber}
        placeholder="+1 (555) 000-0000"
      />

      {/* ── CAPTCHA — Apple-style image challenge ──────────────────────────── */}
      <div>
        {/* Label */}
        <p className="text-[13px] font-medium text-[#1d1d1f] mb-2">
          Type the characters in the image
        </p>

        {/* Image row: distorted-text image + stacked icon buttons */}
        <div className="flex gap-2 mb-3">

          {/* CAPTCHA image */}
          <div className="flex-1 rounded-lg overflow-hidden border border-[#c5d3e8] shadow-[0_1px_4px_rgba(0,0,0,0.07)]">
            <CaptchaImage
              chars={formData.captchaQuestion || ''}
              charData={formData.captchaCharData}
              noiseLines={formData.captchaNoiseLines}
            />
          </div>

          {/* Action buttons — stacked vertically to the right */}
          <div className="flex flex-col gap-1.5">

            {/* Refresh */}
            <button
              type="button"
              onClick={onRefreshCaptcha}
              title="Get a new image"
              aria-label="Refresh CAPTCHA"
              className="w-9 h-9 rounded-lg border border-[#d2d2d7] bg-white hover:bg-[#f5f5f7] active:bg-[#e8e8ed] flex items-center justify-center transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.06)]"
            >
              <svg className="w-4 h-4 text-[#6e6e73]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>

            {/* Audio */}
            <button
              type="button"
              onClick={handleAudio}
              title="Hear the characters read aloud"
              aria-label="Hear CAPTCHA"
              className="w-9 h-9 rounded-lg border border-[#d2d2d7] bg-white hover:bg-[#f5f5f7] active:bg-[#e8e8ed] flex items-center justify-center transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.06)]"
            >
              <svg className="w-4 h-4 text-[#6e6e73]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15.536 8.464a5 5 0 010 7.072M12 6v12m0 0l-3-3m3 3l3-3M9 9a3 3 0 000 6" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728" />
              </svg>
            </button>

          </div>
        </div>

        {/* Answer input */}
        <Input
          name="captchaAnswer"
          type="text"
          value={formData.captchaAnswer}
          onChange={onChange}
          error={errors.captchaAnswer}
          placeholder="Enter the characters above"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck="false"
        />
      </div>
      {/* ──────────────────────────────────────────────────────────────────── */}

      <p className="text-[13px] text-[#6e6e73]">
        We'll send account verification and updates to your work email.
      </p>

    </div>
  );
};

export default MsspContactStep;
